import type { BookingStage } from '@/api/bookings';
import { stageTrack, type StageDates } from '@core/bookings/stages';
import { StatusBadge } from '@/components/StatusBadge';
import { formatDate } from '@core/format/dates';
import { humanizeEnum } from '@core/format/text';
import { stageTone } from '@core/bookings/tones';

export type { StageDates };

/**
 * Where the booking is, across the top of its detail page.
 *
 * <p>
 * <strong>Six pips, not ten — and the four that are missing are the point.</strong> Ready, Issued,
 * Returned and Inspected are facts about individual garments, not about the booking, so they live
 * in the party table below where each garment carries its own. A booking whose sherwani is ready
 * and whose tuxedo is at the tailor has no single answer to "is it ready", and a stepper that
 * demanded one would have to pick a garment and lie about the rest.
 * </p>
 *
 * <p>
 * That is the three-track split made visible by where things are drawn rather than by
 * documentation, which is the only way a structural decision survives contact with the next
 * person to open the file.
 * </p>
 *
 * <p>
 * An exit — cancelled, expired, or gone to recovery — is not a seventh pip. It stops the track
 * where it stopped and says so beside it, because "this booking ended at Confirmed" is the useful
 * sentence and a greyed-out run of future stages implies they are still coming.
 * </p>
 */
export function StageTracker({ stage, dates = {} }: { stage: BookingStage; dates?: StageDates }) {
  // The rule — six steps, an exit stopping where it stopped — is shared with the phone.
  const { steps, exited } = stageTrack(stage, dates);

  return (
    <div className="flex flex-wrap items-start gap-x-2 gap-y-3">
      <ol className="flex min-w-0 flex-1 overflow-x-auto pt-1">
        {steps.map((step, index) => (
          <Step
            key={step.stage}
            stage={step.stage}
            on={step.on}
            state={step.state}
            last={index === steps.length - 1}
            // An exited booking never reaches what it did not reach, so nothing after the stop is
            // drawn as pending — the rail simply ends.
            muted={step.muted}
          />
        ))}
      </ol>

      {exited && (
        <div className="pt-1">
          <StatusBadge tone={stageTone(stage)}>{humanizeEnum(stage)}</StatusBadge>
        </div>
      )}
    </div>
  );
}

function Step({
  stage,
  on,
  state,
  last,
  muted,
}: {
  stage: BookingStage;
  on: string | null;
  state: 'done' | 'current' | 'upcoming';
  last: boolean;
  muted: boolean;
}) {
  const filled = !muted && state === 'done';
  const current = !muted && state === 'current';

  return (
    <li className="relative flex min-w-[92px] flex-col items-center gap-1.5">
      {!last && (
        <span
          aria-hidden
          className={`absolute top-[9px] left-1/2 h-0.5 w-full ${
            filled ? 'bg-primary' : 'bg-border-color'
          }`}
        />
      )}

      <span
        className={`relative z-1 flex size-5 items-center justify-center rounded-full border-2 bg-white ${
          filled
            ? 'border-primary bg-primary'
            : current
              ? 'border-primary ring-primary-transparent ring-3'
              : 'border-border-color'
        }`}
      >
        {filled && <i className="icon icon-check text-[10px] text-white" />}
      </span>

      <span
        className={`text-center text-[11.5px] leading-tight ${
          filled || current ? 'font-medium text-gray-900' : 'text-default'
        }`}
      >
        {humanizeEnum(stage)}
      </span>

      {on && <span className="text-center text-[10.5px] text-default">{formatDate(on)}</span>}
    </li>
  );
}
